import { arrayFilterOut, arrayUnique } from 'common/utilities/core/array';
import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import useWindowResize from 'common/utilities/hooks/useWindowResize/useWindowResize';
import { enqueueInfoNotification } from 'core/redux/actions/notification/notificationActions';
import { copyToClipboardAction } from 'core/redux/actions/ui/uiActions';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import type { EnhancedCommand } from 'core/utilities/core.types';
import I18n from 'i18n/en-US';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createHitList } from 'sequenceAnalyser/api/api';
import type {
EntitySequence as Sequence,   SequenceAlign, SequenceFeatureColor,
Session as SequenceAnalyserSession, } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type {
  HandleAlignedSequencesChange,
  HandleFeatureDefinitionsChange,
  HandleInsertSequences, HandleReplaceSequences,
} from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import {
  changeExportToPNGStatus,
  removedSequences, updateShowSequenceNavigatorSetting, updateSidebarPixelWidth, updateVisualisationsSetting,
} from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';
import { moveSequenceDown,moveSequencesToTop, moveSequenceUp } from 'sequenceAnalyser/utilities/move/move';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';
import { removeGapsFromSelectedSequences } from 'sequenceAnalyser/utilities/selectionActions/removeGaps';
import {
  removeSequences, selectedAlignedSequences,
  selectedSequences, sequenceSelectionAsFastaString, sequenceSelectionAsString, sequenceSelectionIDsAsString,
sortAlignedSequences, } from 'sequenceAnalyser/utilities/selectionActions/selectionActions';
import type {
  FeatureDefinitions, FeatureTypesColor, SequenceAnalyserVisualisation,
} from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import useSortBySimilarity from 'sequenceAnalyser/utilities/similarityTree/useSortBySimilarity';

import AlignActionModal from './AlignActionModal/AlignActionModal';
import ColorsModal from './ColorsModal/ColorsModal';
import ExportToPNG, { exportToPNGError } from './ExportToPNG/ExportToPNG';
import FeaturesModal from './FeaturesModal/FeaturesModal';
import FeaturesToCSVFileExportModal from './FeaturesToCSVFileExportModal/FeaturesToCSVFileExportModal';
import FileExportModal from './FileExportModal/FileExportModal';
import type { ManualSequence } from './InsertActionModal/InsertActionModal';
import InsertActionModal from './InsertActionModal/InsertActionModal';
import InsertFromFileModal from './InsertFromFileModal/InsertFromFileModal';
import InsertGapsActionModal from './InsertGapsActionModal/InsertGapsActionModal';
import InsertRemovedSequencesModal from './InsertRemovedSequencesModal/InsertRemovedSequencesModal';
import OppositeStrandsModal from './OppositeStrandsModal/OppositeStrandsModal';
// import PrintOptionsModal from './PrintOptionsModal/PrintOptionsModal';
import SelectionActions from './SelectionActions/SelectionActions';
import SettingsModal from './SettingsModal/SettingsModal';
import SimilarityTreeModal from './SimilarityTreeModal/SimilarityTreeModal';
import { maxSidebarWidth } from './TableActionsHeader/TableActionsHeader';
import useShortcutDeleteGap from './useShortcutDeleteGap';
import useShortcutInsertGap from './useShortcutInsertGap';

export function toggleSequenceAnalyserVisualisation(
  view: SequenceAnalyserVisualisation, activeViews: SequenceAnalyserVisualisation[],
) {
  let newActiveViews: SequenceAnalyserVisualisation[] = [...activeViews];

  if (activeViews.includes(view)) {
    newActiveViews = newActiveViews.filter((v) => v !== view);
  } else {
    newActiveViews.push(view);
  }

  return newActiveViews;
}

function hasSelected(selection: SequenceSelection) { return selection.ids.length > 0; }

interface ComponentProps {
  onAlignedSequencesChange: HandleAlignedSequencesChange;
  onInsertSequences: HandleInsertSequences;
  onReplaceSequences: HandleReplaceSequences;
  alignedSequences: SequenceAlign[];
  sequences: Sequence[];
  rawSequences: Sequence[];
  // onExport: () => void;
  onResetAlignment: () => void;
  expandedFeaturesForSequences: number[];
  onExpandedFeaturesForSequencesChange: (expandedFeaturesForSequences: number[]) => void;
  sessionId: string;
  onColorSchemeChange: (scheme?: SelectedColorScheme) => void;
  colors?: SelectedColorScheme;
  onFeatureDefinitionsChange: HandleFeatureDefinitionsChange;
  featureDefinitions: FeatureDefinitions;
  featureTypesColor: FeatureTypesColor;
  session: SequenceAnalyserSession;
  userColors: SequenceFeatureColor[];
}

function TableActions({
  onAlignedSequencesChange, onInsertSequences,
  alignedSequences, sequences, rawSequences,
  onResetAlignment, expandedFeaturesForSequences, onExpandedFeaturesForSequencesChange, sessionId,
  onReplaceSequences, onColorSchemeChange, colors, session,
  onFeatureDefinitionsChange, featureDefinitions, featureTypesColor, userColors,
}: ComponentProps) {
  const { sequenceType } = session;

  const dispatch = useDispatch<AppDispatch>();

  const sessionExpired = useSelector((state: ApplicationState) => state.user.sessionExpired);
  const exportToPNGStatus = useSelector((state: ApplicationState) => state.sequenceAnalyser.exportToPNGStatus);
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);
  const sidebarPixelWidth = useSelector((state: ApplicationState) => state.sequenceAnalyser.sidebarPixelWidth);
  const removedSequencesState = useSelector((state: ApplicationState) => state.sequenceAnalyser.removedSequences);
  const labelComposition = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings.labelComposition);
  const settings = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings);

  const [manualSequence, setManualSequence] = React.useState<ManualSequence>();
  const [openDuplicate, setOpenDuplicate] = React.useState(false);

  // See SEL11-913
  // const [openInsertManual, setOpenInsertManual] = React.useState(false);
  // const [openInsertSessionStore, setOpenInsertSessionStore] = React.useState(false);

  const [openInsertGaps, setOpenInsertGaps] = React.useState(false);
  const [openInsertRemovedSequences, setOpenInsertRemovedSequences] = React.useState(false);
  const [openInsertFromFile, setOpenInsertFromFile] = React.useState(false);

  const [openAlign, setOpenAlign] = React.useState(false);
  const [openOppositeStrands, setOpenOppositeStrands] = React.useState(false);
  const sidebarResizerHandleWidth = 26;

  const shortcutCopyToClipboardTrigger = useShortcut('ctrl+c', false, {
    disabled: !hasSelected(selection), disableIfWindowSelection: true,
  });

  // const [openPrintOptions, setOpenPrintOptions] = React.useState(false);
  const [openFileExportOptions, setOpenFileExportOptions] = React.useState(false);
  const [openExportFeaturesCSV, setOpenExportFeaturesCSV] = React.useState(false);

  const [openSettings, setOpenSettings] = React.useState(false);
  const [openColors, setOpenColors] = React.useState(false);
  const [openShowHideFeatures, setOpenShowHideFeatures] = React.useState(false);
  const [openSimilarityTree, setOpenSimilarityTree] = React.useState(false);

  const handleResize = (cursorPositionX: number) => {
    dispatch(updateSidebarPixelWidth(cursorPositionX + sidebarResizerHandleWidth));
  };

  const { sortBySimilarity, SortBySimilarityLoading } = useSortBySimilarity();

  useWindowResize(() => {
    // Ensure the sidebar is resized so fit within the screen when the user resize its browser
    const cursorPositionX = sidebarPixelWidth > maxSidebarWidth() ? maxSidebarWidth() : sidebarPixelWidth;
    handleResize(cursorPositionX);
  });

  const copyToClipboard = useCallback((content: 'ids' | 'sequences' | 'fasta') => {
    let str = '';
    let count = selection.ids.length;

    if (content === 'ids') {
      ({ str, count } = sequenceSelectionIDsAsString(sequences, selection));
    } else if (content === 'sequences') {
      str = sequenceSelectionAsString(alignedSequences, selection);
    } else if (content === 'fasta') {
      str = sequenceSelectionAsFastaString(sequences, alignedSequences, selection, labelComposition);
    }

    dispatch(copyToClipboardAction(
      str,
      content === 'ids'
        ? I18n.SequenceAnalyser.TableActions.copiedIdsToClipboard(count)
        : I18n.SequenceAnalyser.TableActions.copiedSequencesToClipboard(count),
    ));
  }, [dispatch, sequences, alignedSequences, selection, labelComposition]);

  React.useEffect(() => {
    if (shortcutCopyToClipboardTrigger) {
      copyToClipboard('sequences');
    }
  }, [shortcutCopyToClipboardTrigger, copyToClipboard]);

  useShortcutInsertGap(
    alignedSequences,
    sequences,
    onAlignedSequencesChange,
  );

  useShortcutDeleteGap(
    alignedSequences,
    sequences,
    onAlignedSequencesChange,
  );

  const handleSelectionActionClick = (command: EnhancedCommand) => {
    switch (command.id) {
      case 'moveTop': {
        onAlignedSequencesChange(moveSequencesToTop(alignedSequences, selection.ids));
        break;
      }
      case 'moveUp': {
        onAlignedSequencesChange(moveSequenceUp(alignedSequences, selection.ids[0]));
        break;
      }
      case 'moveDown': {
        onAlignedSequencesChange(moveSequenceDown(alignedSequences, selection.ids[0]));
        break;
      }
      case 'copyToClipboardIds': {
        copyToClipboard('ids');
        break;
      }
      case 'copyToClipboard': {
        copyToClipboard('sequences');
        break;
      }
      case 'copyToClipboardFasta': {
        copyToClipboard('fasta');
        break;
      }
      // case 'copyToStore': {
      //   const data: SequenceSelectionWithSequenceData = {
      //     ...selection,
      //     createdDate: new Date().toISOString(),
      //     rawSequences: selectedSequences(rawSequences, selection),
      //     sequences: selectedSequences(sequences, selection),
      //     alignedSequences: selectedAlignedSequences(alignedSequences, selection),
      //   };
      //
      //   dispatch(saveItem({
      //     id: LOCAL_STORAGE_SEQUENCES,
      //     data,
      //   }));
      //   dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.TableActions.copiedToStore(selection.ids.length)));
      //   break;
      // }
      case 'removeSelection': {
        const output = removeSequences(alignedSequences, selection);
        onAlignedSequencesChange(output.remainingSequences);
        dispatch(removedSequences(output.removedSequences));
        break;
      }
      case 'removeGaps': {
        const output = removeGapsFromSelectedSequences(selection, alignedSequences, sequences);
        onAlignedSequencesChange(output.updatedAlignedSequences, output.updatedSequences);
        break;
      }
      case 'duplicate': {
        setManualSequence({
          name: `COPY - ${
            selectedSequences(sequences, selection)[0].entityLabel || selectedSequences(sequences, selection)[0].name
          }`,
          residues: selectedSequences(sequences, selection)[0].residues,
          rawSequence: selectedSequences(rawSequences, selection)[0],
          sequence: selectedSequences(sequences, selection)[0],
          alignedSequence: selectedAlignedSequences(alignedSequences, selection)[0],
        });
        setOpenDuplicate(true);
        break;
      }
      // case 'openInsertManual': {
      //   setOpenInsertManual(true);
      //   break;
      // }
      // case 'openInsertSessionStore': {
      //   setOpenInsertSessionStore(true);
      //   break;
      // }
      case 'openInsertGaps': {
        setOpenInsertGaps(true);
        break;
      }
      case 'openInsertRemovedSequences': {
        setOpenInsertRemovedSequences(true);
        break;
      }
      case 'openInsertFromFile': {
        setOpenInsertFromFile(true);
        break;
      }
      case 'openAlign': {
        setOpenAlign(true);
        break;
      }
      case 'exportHitList': {
        let ids = selectedSequences(sequences, selection).map((s) => s.entityQualifiedId).filter((v) => v);
        if (ids.length === 0) ids = sequences.map((s) => s.entityQualifiedId).filter((v) => v);
        createHitList(session.id, ids as string[]);
        break;
      }
      // case 'exportPDF': {
      //   setOpenPrintOptions(true);
      //   break;
      // }
      case 'exportFile': {
        setOpenFileExportOptions(true);
        break;
      }
      case 'exportFeaturesCSV': {
        setOpenExportFeaturesCSV(true);
        break;
      }
      case 'exportPNG': {
        dispatch(changeExportToPNGStatus('init'));
        break;
      }
      case 'resetAlign': {
        onResetAlignment();
        break;
      }
      case 'expandFeaturesSelected': {
        onExpandedFeaturesForSequencesChange(arrayUnique([...expandedFeaturesForSequences, ...selection.ids]));
        break;
      }
      case 'collapseFeaturesSelected': {
        onExpandedFeaturesForSequencesChange(arrayFilterOut(expandedFeaturesForSequences, selection.ids));
        break;
      }
      case 'expandFeaturesAll': {
        onExpandedFeaturesForSequencesChange(sequences.map((s) => s.id));
        break;
      }
      case 'collapseFeaturesAll': {
        onExpandedFeaturesForSequencesChange([]);
        break;
      }
      case 'arrangeBySimilarity': {
        sortBySimilarity(alignedSequences, onAlignedSequencesChange);
        break;
      }
      case 'sortByLabelAsc': {
        const sortedAlignedSequences = sortAlignedSequences(alignedSequences, sequences, labelComposition);
        onAlignedSequencesChange(sortedAlignedSequences);
        dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.TableActions.sortedByLabel));
        break;
      }
      case 'sortByLabelDesc': {
        const sortedAlignedSequences = sortAlignedSequences(alignedSequences, sequences, labelComposition).reverse();
        onAlignedSequencesChange(sortedAlignedSequences);
        dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.TableActions.sortedByLabel));
        break;
      }
      case 'oppositeStrands': {
        setOpenOppositeStrands(true);
        break;
      }
      case 'openSettings': {
        setOpenSettings(true);
        break;
      }
      case 'openColors': {
        setOpenColors(true);
        break;
      }
      case 'openShowHideFeatures': {
        setOpenShowHideFeatures(true);
        break;
      }
      case 'openSequencesNavigator': {
        dispatch(updateShowSequenceNavigatorSetting(true));
        break;
      }
      case 'openSimilarityTree': {
        setOpenSimilarityTree(true);
        break;
      }
      case 'consensus':
      case 'conservation':
      case 'logo': {
        const newViews = toggleSequenceAnalyserVisualisation(
          command.id,
          settings.visualisations,
        );

        dispatch(updateVisualisationsSetting(newViews));
        break;
      }
      default:
    }
  };

  return (
    <>
      {SortBySimilarityLoading}

      <SelectionActions
        removedSequences={removedSequencesState}
        selection={selection}
        onClick={handleSelectionActionClick}
        sessionSequenceType={sequenceType}
        session={session}
        sessionExpired={sessionExpired}
        exportToPNGError={exportToPNGError(alignedSequences)}
        emptySequencesTable={alignedSequences.length === 0}
      />

      {exportToPNGStatus && <ExportToPNG sequenceType={sequenceType} />}

      {openDuplicate && (
        <InsertActionModal
          open={openDuplicate}
          onInsertSequences={onInsertSequences}
          onOpen={(value) => setOpenDuplicate(value)}
          selection={selection}
          alignedSequences={alignedSequences}
          sequences={sequences}
          sessionSequenceType={sequenceType}
          mode="manual"
          manualSequence={manualSequence}
        />
      )}

      {/*{openInsertManual && (*/}
      {/*  <InsertActionModal*/}
      {/*    open={openInsertManual}*/}
      {/*    onInsertSequences={onInsertSequences}*/}
      {/*    onOpen={(value) => setOpenInsertManual(value)}*/}
      {/*    selection={selection}*/}
      {/*    alignedSequences={alignedSequences}*/}
      {/*    sequences={sequences}*/}
      {/*    sessionSequenceType={sequenceType}*/}
      {/*    mode="manual"*/}
      {/*  />*/}
      {/*)}*/}

      {/*{openInsertSessionStore && (*/}
      {/*  <InsertActionModal*/}
      {/*    open={openInsertSessionStore}*/}
      {/*    onInsertSequences={onInsertSequences}*/}
      {/*    onOpen={(value) => setOpenInsertSessionStore(value)}*/}
      {/*    selection={selection}*/}
      {/*    alignedSequences={alignedSequences}*/}
      {/*    sequences={sequences}*/}
      {/*    sessionSequenceType={sequenceType}*/}
      {/*    mode="sessionStore"*/}
      {/*  />*/}
      {/*)}*/}

      {openInsertGaps && (
        <InsertGapsActionModal
          open={openInsertGaps}
          onAlignedSequencesChange={onAlignedSequencesChange}
          onOpen={(value) => setOpenInsertGaps(value)}
          selection={selection}
          alignedSequences={alignedSequences}
          sequences={sequences}
        />
      )}

      {openInsertRemovedSequences && (
        <InsertRemovedSequencesModal
          open={openInsertRemovedSequences}
          onAlignedSequencesChange={onAlignedSequencesChange}
          onOpen={(value) => setOpenInsertRemovedSequences(value)}
          alignedSequences={alignedSequences}
          sequences={sequences}
          removedSequences={removedSequencesState}
        />
      )}

      {openInsertFromFile && (
        <InsertFromFileModal
          open={openInsertFromFile}
          onInsertSequences={onInsertSequences}
          onOpen={setOpenInsertFromFile}
          selection={selection}
          alignedSequences={alignedSequences}
          sessionId={sessionId}
          sequences={sequences}
        />
      )}

      {openAlign && (
        <AlignActionModal
          open={openAlign}
          onOpen={(value) => setOpenAlign(value)}
          alignmentToolLabel={session.alignmentToolLabel}
        />
      )}

      {/*{openPrintOptions && (*/}
      {/*  <PrintOptionsModal*/}
      {/*    open={openPrintOptions}*/}
      {/*    onOpen={(value) => setOpenPrintOptions(value)}*/}
      {/*    onExport={onExport}*/}
      {/*  />*/}
      {/*)}*/}

      {openFileExportOptions && (
        <FileExportModal
          open={openFileExportOptions}
          onOpen={(value) => setOpenFileExportOptions(value)}
          selection={selection}
          rawSequences={rawSequences}
          sequences={sequences}
          alignedSequences={alignedSequences}
          sessionId={sessionId}
          sequenceType={session.sequenceType}
          featureDefinitions={featureDefinitions}
        />
      )}

      {openExportFeaturesCSV && (
        <FeaturesToCSVFileExportModal
          open={openExportFeaturesCSV}
          onOpen={(value) => setOpenExportFeaturesCSV(value)}
          selection={selection}
          rawSequences={rawSequences}
          sequences={sequences}
          alignedSequences={alignedSequences}
          sequenceType={session.sequenceType}
          featureDefinitions={featureDefinitions}
        />
      )}

      {openOppositeStrands && (
        <OppositeStrandsModal
          open={openOppositeStrands}
          onOpen={(value) => setOpenOppositeStrands(value)}
          selection={selection}
          alignedSequences={alignedSequences}
          sequences={rawSequences}
          sessionId={sessionId}
          onReplaceSequences={onReplaceSequences}
        />
      )}

      {openSettings && (
        <SettingsModal
          open={openSettings}
          onOpen={(value) => setOpenSettings(value)}
        />
      )}

      {openColors && (
        <ColorsModal
          open={openColors}
          onOpen={(value) => setOpenColors(value)}
          onColorSchemeChange={onColorSchemeChange}
          colors={colors}
          sessionSequenceType={sequenceType}
        />
      )}

      {openShowHideFeatures && (
        <FeaturesModal
          open={openShowHideFeatures}
          onOpen={(value) => setOpenShowHideFeatures(value)}
          onFeatureDefinitionsChange={onFeatureDefinitionsChange}
          userColors={userColors}
          featureDefinitions={featureDefinitions}
          featureTypesColor={featureTypesColor}
          sequences={sequences}
          sequenceType={sequenceType}
        />
      )}

      {openSimilarityTree && (
        <SimilarityTreeModal
          open={openSimilarityTree}
          onOpen={(value) => setOpenSimilarityTree(value)}
          alignedSequences={alignedSequences}
          sequences={sequences}
          onAlignedSequencesChange={onAlignedSequencesChange}
        />
      )}
    </>
  );
}

export default TableActions;

